import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

interface TeamData {
  team: {
    id: number;
    name: string;
    organization: string;
    sport: string;
    gender: 'mixed' | 'male' | 'female';
  };
  coach_code: string;
  athlete_code: string;
}

interface TeamCreatedModalProps {
  onClose: () => void;
  teamData: TeamData;
}

/**
 * Modal component for displaying team creation success and invitation codes
 */
const TeamCreatedModal: React.FC<TeamCreatedModalProps> = ({ onClose, teamData }) => {
  const { isDark } = useTheme();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(type);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  return (
         <div className="fixed inset-0 bg-gray-800/80 dark:bg-gray-950/80 overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="cgm-card rounded-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <svg className={`w-12 h-12 cgm-icon-success`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
                         <h3 className="text-2xl cgm-text-primary mb-2">
               Team Created Successfully!
             </h3>
            <p className="cgm-text-secondary text-sm">
              {teamData.team.name} has been created in {teamData.team.organization}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium cgm-text-secondary mb-2">
                Coach Invitation Code
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 cgm-input rounded-md font-code text-sm">
                  {teamData.coach_code}
                </code>
                <button
                  onClick={() => copyToClipboard(teamData.coach_code, 'coach')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    copiedCode === 'coach'
                      ? 'bg-green-500/10 dark:bg-green-500/20 shadow-[inset_0_2px_4px_rgba(34,197,94,0.3)] border border-green-500/30 text-green-600 dark:text-green-400'
                      : 'cgm-button shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]'
                  }`}
                >
                  {copiedCode === 'coach' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium cgm-text-secondary mb-2">
                Athlete Invitation Code
              </label>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 cgm-input rounded-md font-code text-sm">
                  {teamData.athlete_code}
                </code>
                <button
                  onClick={() => copyToClipboard(teamData.athlete_code, 'athlete')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    copiedCode === 'athlete'
                      ? 'bg-green-500/10 dark:bg-green-500/20 shadow-[inset_0_2px_4px_rgba(34,197,94,0.3)] border border-green-500/30 text-green-600 dark:text-green-400'
                      : 'cgm-button shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]'
                  }`}
                >
                  {copiedCode === 'athlete' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="mt-6 space-y-4 text-center">
                         <button
               onClick={onClose}
               className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
             >
               Close
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamCreatedModal; 